import { ExpandMore } from "@clockwise/design-system/icons";
import { Menu, Portal, Transition } from "@headlessui/react";
import classNames from "classnames";
import React, { KeyboardEventHandler, useRef, useState } from "react";
import { usePopper } from "react-popper";

const ABOVE_EVERYTHING = 10000;

export type Variant = "default" | "inline" | "elevated";
export type Size = "mini" | "small" | "medium" | "large";

const SIZE_CLASSES_BUTTON: { [s in Size]: string } = {
  mini: "cw-h-6 cw-min-w-6 cw-rounded-md",
  small: "cw-h-7 cw-min-w-7 cw-rounded-lg",
  medium: "cw-h-8 cw-min-w-8 cw-rounded-lg",
  large: "cw-h-10 cw-min-w-10 cw-rounded-xl",
};

const SIZE_CLASSES_CONTENT: { [s in Size]: string } = {
  mini: "cw-text-12 cw-gap-1",
  small: "cw-text-13 cw-gap-[6px]",
  medium: "cw-text-13 cw-gap-[6px]",
  large: "cw-text-15 cw-gap-[6px]",
};

const EXPAND_ICON_SIZES: { [s in Size]: string } = {
  mini: "cw-h-3 cw-w-3 cw-ml-1",
  small: "cw-h-[14px] cw-w-[14px] cw-ml-1",
  medium: "cw-h-[14px] cw-w-[14px] cw-ml-2",
  large: "cw-h-4 cw-w-4 cw-ml-2",
};

const VARIANT_CLASSES: { [v in Variant]: string } = {
  default:
    "cw-border-muted cw-border-solid cw-border cw-bg-default hover:cw-bg-neutral focus:cw-bg-neutral focus:cw-border-default",
  inline:
    "cw-bg-default cw-border cw-border-solid cw-border-[#FFF] hover:cw-bg-neutral focus:cw-bg-neutral-inset focus:cw-border-muted",
  elevated:
    "cw-bg-default cw-border cw-border-solid cw-border-[#FFF] cw-shadow-select hover:cw-bg-neutral focus:cw-shadow-none focus:cw-bg-neutral focus:cw-border-default disabled:cw-shadow-none disabled:cw-border-muted",
};

const VARIANT_PADDING_CLASSES: { [v in Variant]: { [s in Size]: string } } = {
  default: {
    mini: "cw-px-[6px]",
    small: "cw-px-2",
    medium: "cw-px-2",
    large: "cw-px-3",
  },
  inline: {
    mini: "cw-px-1",
    small: "cw-px-1",
    medium: "cw-px-1",
    large: "cw-px-[6px]",
  },
  elevated: {
    mini: "cw-px-[6px]",
    small: "cw-px-2",
    medium: "cw-px-2",
    large: "cw-px-3",
  },
};

type Props = {
  disabled?: boolean;
  fullWidth?: boolean;
  label: string | React.ReactNode;
  maxHeight?: CSSStyleDeclaration["maxHeight"];
  noIcon?: boolean;
  onOpen?: () => void;
  keepOpen?: boolean;
  size?: Size;
  variant?: Variant;
};

export const DropDownMenu: React.FC<Props> = ({
  children,
  disabled,
  fullWidth,
  label,
  maxHeight = "288px",
  noIcon = false,
  onOpen,
  keepOpen = false,
  size = "medium",
  variant = "default",
}) => {
  const wasOpen = useRef(false);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update: updatePopperInstance } = usePopper(trigger, container, {
    placement: "bottom-start",
    strategy: "fixed",
    modifiers: [
      {
        name: "flip",
        enabled: true,
      },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    // DevNote: https://github.com/tailwindlabs/headlessui/discussions/1122
    if (!keepOpen) {
      return;
    }

    if (event.key === "Enter") {
      const activeItemId = menuItemsRef?.current?.getAttribute("aria-activedescendant");

      if (!activeItemId) {
        return;
      }

      const activeItem = document.getElementById(activeItemId);

      if (!activeItem) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      activeItem.click();
    }
  };

  return (
    <Menu>
      {({ open }) => {
        if (open && !wasOpen.current) {
          wasOpen.current = true;
          setTimeout(() => {
            onOpen?.();
            void updatePopperInstance?.();
          }, 0);
        } else if (!open && wasOpen) {
          wasOpen.current = false;
        }
        return (
          <>
            <Menu.Button
              ref={setTrigger}
              className={classNames(
                "cw-font-body",
                {
                  "cw-w-full": fullWidth,
                  "cw-cursor-pointer cw-text-default": !disabled,
                  "cw-pointer-events-none cw-text-default-disabled": disabled,
                },
                SIZE_CLASSES_BUTTON[size],
                VARIANT_CLASSES[variant],
                VARIANT_PADDING_CLASSES[variant][size],
                "cw-font-medium",
                "cw-max-w-full cw-flex cw-justify-center cw-items-center",
              )}
            >
              {label}
              {!noIcon && (
                <span className="cw-block cw-truncate">
                  <div
                    className={classNames(SIZE_CLASSES_CONTENT[size], "cw-flex cw-items-center")}
                  >
                    <ExpandMore
                      className={classNames(EXPAND_ICON_SIZES[size])}
                      aria-hidden="true"
                    />
                  </div>
                </span>
              )}
            </Menu.Button>
            <Portal>
              <div
                ref={setContainer}
                style={{
                  ...styles.popper,
                  minWidth: trigger?.scrollWidth,
                  zIndex: ABOVE_EVERYTHING,
                }}
                {...attributes.popper}
              >
                <Transition
                  leave="cw-transition cw-ease-in cw-duration-100"
                  leaveFrom="cw-opacity-100"
                  leaveTo="cw-opacity-0"
                  enter="cw-transition cw-ease-in cw-duration-100"
                  enterFrom="cw-opacity-0"
                  enterTo="cw-opacity-100"
                >
                  <Menu.Items
                    className={classNames(
                      "cw-z-50 cw-min-w-full cw-p-1 cw-overflow-auto",
                      "cw-font-body cw-font-normal cw-text-13 cw-bg-default cw-rounded-lg cw-shadow-selectPopup cw-min-w-[180px]",
                    )}
                    style={{ maxHeight }}
                    ref={menuItemsRef}
                    onKeyDown={handleKeyDown}
                  >
                    {children}
                  </Menu.Items>
                </Transition>
              </div>
            </Portal>
          </>
        );
      }}
    </Menu>
  );
};
